import { createSupabaseAdminClient } from "@/lib/supabase/admin"
import type { Review } from "@/types/review"

type RatingDistribution = Record<1 | 2 | 3 | 4 | 5, number>

function mapReviewRow(row: {
  id: string
  recipe_id: string
  author_name: string
  rating: number
  text: string
  helpful_count: number
  status: "pending" | "approved" | "rejected"
  created_at: string
}): Review {
  return {
    id: row.id,
    recipeId: row.recipe_id,
    authorName: row.author_name,
    rating: row.rating as 1 | 2 | 3 | 4 | 5,
    text: row.text,
    helpfulCount: row.helpful_count,
    status: row.status,
    createdAt: row.created_at,
  }
}

export async function getApprovedReviewsByRecipeId(
  recipeId: string,
): Promise<Review[]> {
  const supabase = createSupabaseAdminClient()

  const { data, error } = await supabase
    .from("recipe_reviews")
    .select(
      "id, recipe_id, author_name, rating, text, helpful_count, status, created_at",
    )
    .eq("recipe_id", recipeId)
    .eq("status", "approved")
    .order("created_at", { ascending: false })

  if (error || !data) {
    console.error("Failed to load reviews from Supabase:", error?.message)
    return []
  }

  return data.map(mapReviewRow)
}

export async function getRatingSummaryByRecipeId(recipeId: string): Promise<{
  average: number
  count: number
  distribution: RatingDistribution
}> {
  const reviews = await getApprovedReviewsByRecipeId(recipeId)

  const distribution: RatingDistribution = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  }

  if (reviews.length === 0) {
    return {
      average: 0,
      count: 0,
      distribution,
    }
  }

  let total = 0

  for (const review of reviews) {
    total += review.rating
    distribution[review.rating] += 1
  }

  const average = Math.round((total / reviews.length) * 10) / 10

  return {
    average,
    count: reviews.length,
    distribution,
  }
}
